import React from 'react';
import { HbrButton, HbrDomTable } from '@harbor/elements-react-standalone';
import { Column, ColumnAlignment, ColumnType, DataType, getColumnTypeConfig } from '../../../types/table';
import { formatDateOrTBD } from '../../../support/utils';

interface Props {
  rows: any[];
  onOpenSemNotes: (row: any) => void;
  onOpenDetails: (row: any) => void;
}

// Percentage widths (sum ~100%) rather than fixed px: HbrDomTable's table is width:100%, so percent
// columns shrink/grow WITH the nested container and never force horizontal scroll (fixed px widths
// become hard min-widths whose sum — plus w-max headers — overflowed the expandable row).
const cols: Column[] = [
  { id: 'product_name', name: 'Product', width: '22%', columnType: ColumnType.String, dataType: DataType.String, sortable: false, align: ColumnAlignment.Left, resizable: false, reorderable: false },
  { id: 'status', name: 'Status', width: '10%', columnType: ColumnType.String, dataType: DataType.String, sortable: false, align: ColumnAlignment.Left, resizable: false, reorderable: false },
  { id: 'pov_kickoff_date', name: 'Kickoff', width: '11%', columnType: ColumnType.Date, dataType: DataType.Date, sortable: false, align: ColumnAlignment.Left, resizable: false, reorderable: false },
  { id: 'expected_complete_date', name: 'Expected Complete', width: '15%', columnType: ColumnType.Date, dataType: DataType.Date, sortable: false, align: ColumnAlignment.Left, resizable: false, reorderable: false },
  { id: 'qualified', name: 'Qualified', width: '10%', columnType: ColumnType.String, dataType: DataType.String, sortable: false, align: ColumnAlignment.Left, resizable: false, reorderable: false },
  { id: 'risk', name: 'Risk', width: '8%', columnType: ColumnType.String, dataType: DataType.String, sortable: false, align: ColumnAlignment.Left, resizable: false, reorderable: false },
  { id: 'sem_notes', name: 'SEM Notes', width: '14%', columnType: ColumnType.Custom, dataType: DataType.String, sortable: false, align: ColumnAlignment.Left, resizable: false, reorderable: false },
  { id: 'details', name: '', width: '10%', columnType: ColumnType.Custom, dataType: DataType.String, sortable: false, align: ColumnAlignment.Left, resizable: false, reorderable: false },
];

/** Kickoff as a sortable time; null when blank, unparseable or a placeholder date (shown as TBD). */
function kickoffTime(row: any): number | null {
  const v = row?.pov_kickoff_date;
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) || d.getFullYear() < 2002 ? null : d.getTime();
}

/** Product rows by Kickoff, most recent first; products with no kickoff date go last, in their original order. */
export function sortByKickoffDesc<T>(rows: T[]): T[] {
  return (rows || [])
    .map((row, i) => ({ row, i, t: kickoffTime(row) }))
    .sort((a, b) => (a.t === null) !== (b.t === null) ? (a.t === null ? 1 : -1) : (b.t ?? 0) - (a.t ?? 0) || a.i - b.i)
    .map((x) => x.row);
}

const DATE_FIELDS = new Set(['pov_kickoff_date', 'expected_complete_date', 'expected_close_date']);

export default function PovOpportunityProductRowsTable({ rows, onOpenSemNotes, onOpenDetails }: Props) {
  const columns = cols.map((c) => getColumnTypeConfig(c));
  const data = sortByKickoffDesc(rows || []).map((r: any) => ({
    ...r,
    id: r.row_id,
    product_name: r?.product?.name || '',
  }));

  const renderHeader = () =>
    columns.map((column) => (
      <div key={`column-${column.id}`} slot={`column-${column.id}`}>
        <div
          className="flex items-center relative"
          style={{ justifyContent: column.align === ColumnAlignment.Right ? 'flex-end' : 'flex-start' }}
        >
          <div>{column.name}</div>
        </div>
      </div>
    ));

  const renderBody = () => {
    const list: JSX.Element[] = [];
    for (const row of data) {
      for (const col of columns as any[]) {
        const slot = `row-${row.id}-column-${col.id}`;

        if (col.id === 'sem_notes') {
          const count = Array.isArray(row.sem_notes) ? row.sem_notes.length : 0;
          list.push(
            <div key={slot} slot={slot} className="min-h-8 flex items-center box-content">
              <button className="text-link hover:underline" onClick={() => onOpenSemNotes(row)}>
                {count} Notes
              </button>
            </div>
          );
          continue;
        }

        if (col.id === 'details') {
          list.push(
            <div key={slot} slot={slot} className="min-h-8 flex items-center box-content">
              <HbrButton size="small" variant="outline" onClick={() => onOpenDetails(row)}>
                Details
              </HbrButton>
            </div>
          );
          continue;
        }

        let v = row[col.id];
        if (DATE_FIELDS.has(col.id)) {
          v = formatDateOrTBD(v);
        }

        list.push(
          <div key={slot} slot={slot} className="min-h-8 flex items-center box-content">
            {v ?? ''}
          </div>
        );
      }
    }
    return list;
  };

  return (
    <HbrDomTable
      autoLayout={true}
      columns={columns as any}
      data={data}
      style={{ display: 'block', width: '100%' }}
    >
      {renderHeader()}
      {renderBody()}
    </HbrDomTable>
  );
}